/** Source-local desktop audio evidence; never a published RTP or program PTS. */
export interface ShareAudioTranscriptEvidence {
  version: 1;
  sourceId: string;
  sourceEpoch: string;
  segmentId: string;
  clockId: string;
  clockDomain: 'browser_audio_samples' | 'unavailable';
  sampleRateHz: number | null;
  startSample: number | null;
  endSample: number | null;
  captureStartedAtMs: number | null;
  captureEndedAtMs: number | null;
  captureMonotonicStartMs: number | null;
  captureMonotonicEndMs: number | null;
  timestampBasis: 'vad_callback_estimate' | 'unavailable';
  /** VAD callback arrival does not bound source capture or presentation delay. */
  uncertaintyMs: null;
  /** Browser receive time, separate from both acquisition and server receipt. */
  transcriptReceivedAtMs: number;
  /** Provider completion time is unavailable in the current STT adapters. */
  inferenceCompletedAtMs: null;
}

const identifier = (value: unknown): value is string => (
  typeof value === 'string' && /^[a-zA-Z0-9._:-]{1,200}$/.test(value)
);
const finiteTime = (value: unknown): value is number => (
  typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= Number.MAX_SAFE_INTEGER
);

/** Validate untrusted activity metadata without granting source authorization. */
export function parseShareAudioTranscriptEvidence(value: unknown): ShareAudioTranscriptEvidence | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const data = value as Record<string, unknown>;
  if (
    data.version !== 1 || !identifier(data.sourceId) || !identifier(data.sourceEpoch) ||
    !identifier(data.segmentId) || !identifier(data.clockId) ||
    data.uncertaintyMs !== null || data.inferenceCompletedAtMs !== null ||
    !finiteTime(data.transcriptReceivedAtMs) || data.transcriptReceivedAtMs === 0
  ) return null;

  const available = data.clockDomain === 'browser_audio_samples';
  const timingFields = [data.sampleRateHz, data.startSample, data.endSample, data.captureStartedAtMs,
    data.captureEndedAtMs, data.captureMonotonicStartMs, data.captureMonotonicEndMs];
  if (available) {
    if (
      data.timestampBasis !== 'vad_callback_estimate' || data.sampleRateHz !== 16_000 ||
      !finiteTime(data.startSample) || !Number.isSafeInteger(data.startSample) ||
      !finiteTime(data.endSample) || !Number.isSafeInteger(data.endSample) || data.endSample <= data.startSample ||
      data.endSample - data.startSample > 16_000 * 600 ||
      !finiteTime(data.captureStartedAtMs) || !finiteTime(data.captureEndedAtMs) ||
      data.captureEndedAtMs < data.captureStartedAtMs ||
      data.captureEndedAtMs - data.captureStartedAtMs > 600_000 ||
      !finiteTime(data.captureMonotonicStartMs) || !finiteTime(data.captureMonotonicEndMs) ||
      data.captureMonotonicEndMs < data.captureMonotonicStartMs ||
      data.captureMonotonicEndMs - data.captureMonotonicStartMs > 600_000
    ) return null;
  } else if (
    data.clockDomain !== 'unavailable' || data.timestampBasis !== 'unavailable' ||
    timingFields.some((field) => field !== null)
  ) return null;

  // The checks above narrow related fields as a group. Copy only the contract,
  // discarding arbitrary metadata such as a client-claimed program timestamp.
  return {
    version: 1,
    sourceId: data.sourceId,
    sourceEpoch: data.sourceEpoch,
    segmentId: data.segmentId,
    clockId: data.clockId,
    clockDomain: data.clockDomain,
    sampleRateHz: data.sampleRateHz,
    startSample: data.startSample,
    endSample: data.endSample,
    captureStartedAtMs: data.captureStartedAtMs,
    captureEndedAtMs: data.captureEndedAtMs,
    captureMonotonicStartMs: data.captureMonotonicStartMs,
    captureMonotonicEndMs: data.captureMonotonicEndMs,
    timestampBasis: data.timestampBasis,
    uncertaintyMs: null,
    transcriptReceivedAtMs: data.transcriptReceivedAtMs,
    inferenceCompletedAtMs: null,
  } as ShareAudioTranscriptEvidence;
}

/** Canonical stage playback identity; separate from the browser STT capture epoch. */
export interface StageAudioSource {
  sourceId: 'stage-media:mp4' | 'stage-media:hls';
  sourceIncarnation: string;
}
export function parseStageAudioSource(value: unknown): StageAudioSource | null {
  if (!value || typeof value !== 'object') return null;
  const source = value as Record<string, unknown>;
  return (source.sourceId === 'stage-media:mp4' || source.sourceId === 'stage-media:hls') &&
    identifier(source.sourceIncarnation) ? { sourceId: source.sourceId, sourceIncarnation: source.sourceIncarnation } : null;
}
